import localforage from 'localforage';

const cardStore = localforage.createInstance({
  name: 'phoenixBingo',
  storeName: 'cards',
});

const API = process.env.REACT_APP_API;

/**
 * Get the current card set from localStorage.
 */
function getCardSet() {
  return localStorage.getItem('cardSet') || 'v1';
}

/**
 * Fetch all cards and cache them in localforage.
 * Returns the full array of card objects.
 */
export async function loadAndCacheCards() {
  const cardSet = getCardSet();
  const cacheKey = `allCards_${cardSet}`;

  // Try cache first
  const cached = await cardStore.getItem(cacheKey);
  if (cached && cached.length > 0) {
    return cached;
  }

  // Fetch from server
  const response = await fetch(`${API}/card/getCards?cardSet=${cardSet}`);
  const cards = await response.json();

  // Cache locally
  await cardStore.setItem(cacheKey, cards);
  return cards;
}

/**
 * Get a single card by palette number from cache.
 * Falls back to fetching + caching if not found.
 */
export async function getCardByPaletteNumber(paletteNumber) {
  const cards = await loadAndCacheCards();
  return cards.find((c) => c.paletteNumber == paletteNumber) || null;
}

/**
 * Force refresh the card cache (call after admin adds/edits cards).
 */
export async function refreshCardCache() {
  const cardSet = getCardSet();
  const cacheKey = `allCards_${cardSet}`;
  const response = await fetch(`${API}/card/getCards?cardSet=${cardSet}`);
  const cards = await response.json();
  await cardStore.setItem(cacheKey, cards);
  return cards;
}

/**
 * Check if a card is a winner given the called numbers.
 * Mirrors the server's isWinner() logic exactly.
 *
 * @param {Object} card - Card object with card.numbers.{B,I,N,G,O}
 * @param {string[]} calledNumbers - Array of called numbers like ["B1", "I16", "N31"]
 * @returns {{ isWinner: boolean, pattern: string|null }}
 */
export function checkWin(card, calledNumbers) {
  const nums = card.numbers;
  const cols = ['B', 'I', 'N', 'G', 'O'];

  // Check rows
  for (let i = 0; i < 5; i++) {
    const rowMatch = cols.every((col) => {
      const num = nums[col][i];
      return num === 'FREE' || calledNumbers.includes(`${col}${num}`);
    });
    if (rowMatch) return { isWinner: true, pattern: `row${i + 1}` };
  }

  // Check columns
  for (const col of cols) {
    const colMatch = nums[col].every(
      (num) => num === 'FREE' || calledNumbers.includes(`${col}${num}`)
    );
    if (colMatch) return { isWinner: true, pattern: `col${col}` };
  }

  // Check diagonals
  const diag1 = [nums.B[0], nums.I[1], nums.N[2], nums.G[3], nums.O[4]];
  if (
    diag1.every(
      (num, idx) => num === 'FREE' || calledNumbers.includes(`${cols[idx]}${num}`)
    )
  ) {
    return { isWinner: true, pattern: 'diag1' };
  }

  const diag2 = [nums.B[4], nums.I[3], nums.N[2], nums.G[1], nums.O[0]];
  if (
    diag2.every(
      (num, idx) => num === 'FREE' || calledNumbers.includes(`${cols[idx]}${num}`)
    )
  ) {
    return { isWinner: true, pattern: 'diag2' };
  }

  // Check corners
  const corners = [nums.B[0], nums.B[4], nums.O[0], nums.O[4]];
  if (
    corners.every(
      (num) => calledNumbers.includes(`B${num}`) || calledNumbers.includes(`O${num}`)
    )
  ) {
    return { isWinner: true, pattern: 'corners' };
  }

  // Check surrounding FREE
  const surr = [nums.I[1], nums.I[3], nums.G[1], nums.G[3]];
  if (
    surr.every(
      (num) => calledNumbers.includes(`I${num}`) || calledNumbers.includes(`G${num}`)
    )
  ) {
    return { isWinner: true, pattern: 'surroundingFree' };
  }

  return { isWinner: false, pattern: null };
}
